import { supabase } from '../lib/supabaseClient';

/**
 * Marcados con GPS, para dibujarlos sobre el mapa de tiendas.
 *
 * PARA QUÉ SIRVE VERLOS: la coordenada de una tienda se fija a mano (enlace de mapa) o desde el
 * terreno, y hasta ahora no había forma de saber si quedó BIEN salvo esperar a que alguien
 * reclamara una alerta falsa. Con los marcados encima del círculo de la geocerca se ve de un
 * vistazo lo que antes había que deducir: si la nube de puntos cae centrada, la coordenada es
 * buena; si cae pegada al borde o afuera, la mal puesta es la TIENDA, no la persona.
 */
export interface MarcadoGeo {
  id: string;
  pdv_id: string;
  kind: 'ingreso' | 'salida';
  server_ts: string;
  lat: number;
  lng: number;
  accuracy: number | null;
  flags: string[];
  persona: string;
  dni: string;
}

interface MarcadoCrudo {
  id: string;
  kind: 'ingreso' | 'salida';
  server_ts: string;
  lat: number | null;
  lng: number | null;
  accuracy: number | null;
  flags: string[] | null;
  assignment: {
    worker: { dni: string; first_names: string; last_names: string } | null;
    position: { pdv_id: string } | null;
  } | null;
}

/** Tope de puntos a traer. El mapa se vuelve ilegible mucho antes de este número. */
const TOPE = 1000;

/**
 * Trae los marcados con coordenada, del más reciente hacia atrás.
 *
 * NO pagina como el resto de lecturas (§5 de la bitácora) y es a propósito: acá una lectura
 * incompleta no esconde trabajo pendiente ni descuadra un pago, solo dibuja menos puntos. Por
 * eso devuelve además `hayMas`, para poder DECIRLO en pantalla en vez de simular que eso es
 * todo lo que hay.
 */
export async function listMarcadosConGps(
  limite = 300,
): Promise<{ marcados: MarcadoGeo[]; hayMas: boolean }> {
  const tope = Math.min(limite, TOPE);
  const { data, error } = await supabase
    .from('attendance')
    .select(
      `id, kind, server_ts, lat, lng, accuracy, flags,
       assignment:assignments!inner(
         worker:profiles!assignments_worker_id_fkey(dni, first_names, last_names),
         position:positions!inner(pdv_id))`,
    )
    .not('lat', 'is', null)
    .order('server_ts', { ascending: false })
    .limit(tope + 1);

  if (error) throw new Error(error.message);

  const filas = (data ?? []) as unknown as MarcadoCrudo[];
  const hayMas = filas.length > tope;

  const marcados = filas.slice(0, tope).flatMap((f): MarcadoGeo[] => {
    const pdvId = f.assignment?.position?.pdv_id;
    // Sin tienda no hay dónde dibujarlo. Bajo RLS un embed sin permiso devuelve null en vez de
    // fallar (§3.7), así que esto no es paranoia: es el caso real de un coordinador mirando
    // marcados de una campaña que no es suya.
    if (!pdvId || f.lat === null || f.lng === null) return [];
    const w = f.assignment?.worker;
    return [
      {
        id: f.id,
        pdv_id: pdvId,
        kind: f.kind,
        server_ts: f.server_ts,
        lat: f.lat,
        lng: f.lng,
        accuracy: f.accuracy,
        flags: f.flags ?? [],
        // El nombre puede no verse por RLS; el punto igual se dibuja, porque la ubicación es
        // el dato que interesa acá y esconderlo dejaría un hueco en el mapa sin explicación.
        persona: w ? `${w.first_names} ${w.last_names}` : 'Recurso no visible',
        dni: w?.dni ?? '',
      },
    ];
  });

  return { marcados, hayMas };
}
